'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

interface WeatherData {
  temperature: number;
  condition: string;
  icon: string;
  windSpeed: number;
  humidity: number;
  feelsLike: number;
}

interface DailyForecast {
  date: string;
  dayName: string;
  maxTemp: number;
  minTemp: number;
  icon: string;
  condition: string;
  precipitationChance: number;
}

interface WeatherWidgetProps {
  lat: number;
  lng: number;
  locationName: string;
  compact?: boolean;
}

export default function WeatherWidget({ lat, lng, locationName, compact = false }: WeatherWidgetProps) {
  const t = useTranslations('weather');
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchWeather() {
      try {
        setLoading(true);
        setError(null);

        // Using Open-Meteo API (free, no API key required)
        // Include daily forecast for 3 days
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current_weather=true&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min,weathercode,precipitation_probability_max&forecast_days=3&timezone=auto`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch weather');
        }

        const data = await response.json();
        const current = data.current_weather;
        const daily = data.daily;

        // Map weather codes to conditions
        const getCondition = (code: number): { text: string; icon: string } => {
          if (code === 0) return { text: 'Słonecznie', icon: '☀️' };
          if (code <= 3) return { text: 'Częściowe zachmurzenie', icon: '⛅' };
          if (code <= 48) return { text: 'Mgła', icon: '🌫️' };
          if (code <= 67) return { text: 'Deszcz', icon: '🌧️' };
          if (code <= 77) return { text: 'Śnieg', icon: '❄️' };
          if (code <= 82) return { text: 'Przelotne opady', icon: '🌦️' };
          if (code <= 99) return { text: 'Burza', icon: '⛈️' };
          return { text: 'Zmienne', icon: '🌤️' };
        };

        const condition = getCondition(current.weathercode);

        setWeather({
          temperature: Math.round(current.temperature),
          condition: condition.text,
          icon: condition.icon,
          windSpeed: Math.round(current.windspeed),
          humidity: data.hourly.relative_humidity_2m[0] || 0,
          feelsLike: Math.round(current.temperature), // Approximate
        });

        // Process daily forecast
        const forecastData: DailyForecast[] = daily.time.map((date: string, index: number) => {
          const d = new Date(date);
          const dayNames = ['Niedz', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob'];
          const dayName = index === 0 ? 'Dziś' : index === 1 ? 'Jutro' : dayNames[d.getDay()];
          const forecastCondition = getCondition(daily.weathercode[index]);

          return {
            date,
            dayName,
            maxTemp: Math.round(daily.temperature_2m_max[index]),
            minTemp: Math.round(daily.temperature_2m_min[index]),
            icon: forecastCondition.icon,
            condition: forecastCondition.text,
            precipitationChance: daily.precipitation_probability_max[index] || 0,
          };
        });

        setForecast(forecastData);
      } catch (err) {
        setError(t('fetchError'));
        console.error('Weather fetch error:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchWeather();
  }, [lat, lng]);

  if (loading) {
    return (
      <div className="weather-widget loading">
        <div className="weather-widget-content">
          <div className="weather-icon">🌤️</div>
          <div className="weather-text">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="weather-widget error">
        <div className="weather-widget-content">
          <div className="weather-icon">❌</div>
          <div className="weather-text">{error || t('noData')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`weather-widget ${compact ? 'compact' : ''}`}>
      {!compact && (
        <h3 className="weather-widget-title">
          {t('currentWeatherIn', { location: locationName })}
        </h3>
      )}

      <div className="weather-widget-content">
        <div className="weather-main">
          <div className={compact ? "weather-icon" : "weather-icon-large"}>{weather.icon}</div>
          <div className="weather-temp">{weather.temperature}°C</div>
          <div className="weather-condition">{weather.condition}</div>
        </div>

        <div className="weather-details">
          <div className="weather-detail-item">
            <span className="weather-detail-label">Odczuwalna:</span>
            <span className="weather-detail-value">{weather.feelsLike}°C</span>
          </div>
          <div className="weather-detail-item">
            <span className="weather-detail-label">Wiatr:</span>
            <span className="weather-detail-value">{weather.windSpeed} km/h</span>
          </div>
          <div className="weather-detail-item">
            <span className="weather-detail-label">Wilgotność:</span>
            <span className="weather-detail-value">{weather.humidity}%</span>
          </div>
        </div>
      </div>

      {forecast.length > 0 && (
        <div className="weather-forecast">
          {!compact && <h4 className="forecast-title">Prognoza 3-dniowa</h4>}
          <div className="forecast-days">
            {forecast.map((day) => (
              <div key={day.date} className="forecast-day">
                <div className="forecast-day-name">{day.dayName}</div>
                <div className="forecast-icon">{day.icon}</div>
                <div className="forecast-temps">
                  <span className="forecast-temp-max">{day.maxTemp}°</span>
                  <span className="forecast-temp-min">{day.minTemp}°</span>
                </div>
                {!compact && day.precipitationChance > 0 && (
                  <div className="forecast-precipitation">
                    💧 {day.precipitationChance}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        .weather-widget {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 20px;
          color: white;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
          margin: 20px 0;
        }

        .weather-widget.compact {
          padding: 8px;
          margin: 0;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .weather-widget.loading,
        .weather-widget.error {
          background: linear-gradient(135deg, #858585 0%, #5a5a5a 100%);
        }

        .weather-widget-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 12px 0;
          opacity: 0.9;
        }

        .weather-widget-content {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .weather-widget.compact .weather-widget-content {
          gap: 8px;
        }

        .weather-main {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .weather-widget.compact .weather-main {
          gap: 2px;
        }

        .weather-icon {
          font-size: 28px;
        }

        .weather-icon-large {
          font-size: 56px;
          line-height: 1;
        }

        .weather-widget.compact .weather-icon-large,
        .weather-widget.compact .weather-icon {
          font-size: 20px;
        }

        .weather-temp {
          font-size: 38px;
          font-weight: 700;
          line-height: 1;
        }

        .weather-widget.compact .weather-temp {
          font-size: 18px;
        }

        .weather-condition {
          font-size: 14px;
          font-weight: 500;
          opacity: 0.9;
        }

        .weather-widget.compact .weather-condition {
          font-size: 10px;
        }

        .weather-text {
          font-size: 16px;
          text-align: center;
        }

        .weather-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .weather-widget.compact .weather-details {
          gap: 4px;
        }

        .weather-detail-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .weather-widget.compact .weather-detail-item {
          padding: 3px 0;
          border-bottom: none;
        }

        .weather-detail-item:last-child {
          border-bottom: none;
        }

        .weather-detail-label {
          font-size: 14px;
          opacity: 0.8;
        }

        .weather-widget.compact .weather-detail-label {
          font-size: 10px;
        }

        .weather-detail-value {
          font-size: 16px;
          font-weight: 600;
        }

        .weather-widget.compact .weather-detail-value {
          font-size: 11px;
        }

        .weather-forecast {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .weather-widget.compact .weather-forecast {
          display: none;
        }

        .forecast-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 16px 0;
          opacity: 0.9;
        }

        .forecast-days {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .weather-widget.compact .forecast-days {
          gap: 6px;
        }

        .forecast-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .weather-widget.compact .forecast-day {
          padding: 8px 6px;
          gap: 3px;
        }

        .forecast-day:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
        }

        .forecast-day-name {
          font-size: 14px;
          font-weight: 600;
          opacity: 0.9;
        }

        .weather-widget.compact .forecast-day-name {
          font-size: 11px;
        }

        .forecast-icon {
          font-size: 32px;
          line-height: 1;
        }

        .weather-widget.compact .forecast-icon {
          font-size: 20px;
        }

        .forecast-temps {
          display: flex;
          gap: 8px;
          align-items: center;
          font-size: 16px;
          font-weight: 600;
        }

        .forecast-temp-max {
          color: white;
        }

        .forecast-temp-min {
          opacity: 0.7;
        }

        .forecast-precipitation {
          font-size: 12px;
          opacity: 0.8;
        }

        @media (max-width: 640px) {
          .weather-widget {
            padding: 16px;
          }

          .weather-widget-content {
            flex-direction: column;
            gap: 16px;
          }

          .weather-details {
            width: 100%;
          }

          .weather-temp {
            font-size: 40px;
          }

          .weather-icon-large {
            font-size: 56px;
          }

          .forecast-days {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .forecast-day {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
}
